/* Themed Personalised Lolly Bags V2
   Canopy handles cart submission.
   Globo handles themed options, validation and label add-ons.
*/

(() => {
  'use strict';

  if (window.LollyThemedV2) {
    window.LollyThemedV2.init(document);
    return;
  }

  const instances = new Map();

  function mount(root) {
    const sectionId = root.dataset.section;
    const builder = root.querySelector('.lolly-themed-builder');

    const dataNode = root.querySelector(
      '[id^="lolly-builder-data-"]'
    );

    const designData = root.querySelector(
      '[id^="lolly-design-data-"]'
    );

    if (!builder || !dataNode) return null;

    if (builder.dataset.controllerStarted === 'true') {
      return null;
    }

    let destroyed = false;

    const aborter = new AbortController();

    function listen(target, name, handler, options = {}) {
      const settings = typeof options === 'boolean'
        ? { capture: options }
        : options;

      target.addEventListener(name, handler, {
        ...settings,
        signal: aborter.signal
      });
    }

    builder.dataset.controllerStarted = 'true';
    builder.dataset.cartReady = 'false';

    const find = selector => builder.querySelector(selector);
    const statusNode = find('[data-lb-status]');
    const packsNode = find('[data-lb-packs]');

    let data;
    let products;
    let form;
    let app;
    let hostOption;
    let activeProduct;
    let activeVariant;

    let busy = false;
    let failed = false;
    let initialised = false;
    let previewPrepared = false;

    let priceObserver;
    let startupTimer;
    let summaryTimer;
    let summaryPending = false;

    function status(message) {
      if (!statusNode) return;

      if (statusNode.textContent !== message) {
        statusNode.textContent = message;
      }

      if (statusNode.hidden !== !message) {
        statusNode.hidden = !message;
      }
    }

    function put(selector, text) {
      const node = find(selector);

      if (node && node.textContent !== text) {
        node.textContent = text;
      }
    }

    function blockCart() {
      builder.dataset.cartReady = 'false';

      form?.querySelectorAll(
        '[name="add"], button[type="submit"], input[type="submit"]'
      ).forEach(button => {
        button.disabled = true;
      });
    }

    function fail(error) {
      failed = true;
      blockCart();
      clearInterval(startupTimer);

      status(
        `${error.message} Refresh the page before trying again.`
      );

      console.error('Themed lolly bag builder:', error);
    }

    try {
      data = JSON.parse(dataNode.textContent);

      products = (data.products || []).filter(product =>
        product &&
        String(product.id) !== String(data.hostId) &&
        Array.isArray(product.variants) &&
        product.variants.length
      );
    } catch (error) {
      status('The builder product data could not be loaded.');
      console.error('Themed lolly bag builder:', error);
      return null;
    }

    const productMap = new Map(
      products.map(product => [product.handle, product])
    );

    const labelVariants = Array.isArray(data.labels)
      ? data.labels
      : [];

    const formatter = new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: data.currency || 'NZD'
    });

    const money = cents => formatter.format(cents / 100);

    const shortName = product =>
      String(product.title || '')
        .replace(/\s*\(Personalised.*$/i, '')
        .trim();

    function packCount(variant) {
      const count = String(variant.title).match(
        /^\s*([\d,]+)\s*bags?\b/i
      )?.[1];

      return count ? count.replaceAll(',', '') : null;
    }

    function packLabel(variant) {
      const count = packCount(variant);

      return count
        ? `${Number(count).toLocaleString('en-NZ')} bags`
        : variant.title;
    }

    function contentsDescription(value) {
      let text = String(value || '')
        .replace(/^\s*[\d,]+\s*bags?\b/i, '')
        .replace(/^[\s,;:–—-]+/, '')
        .trim();

      if (text.startsWith('(') && text.endsWith(')')) {
        text = text.slice(1, -1).trim();
      }

      if (!text) return '';

      text = text
        .replace(/^approx(?:imately)?\.?\s*/i, 'Approx ')
        .replace(/\s+(?:ea\.?|each)\s*\.?$/i, ' per bag')
        .replace(/\.\s*$/, '')
        .trim();

      if (!/\bper\s+bag\b/i.test(text)) {
        text += ' per bag';
      }

      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    function selectedQuantity() {
      return Math.max(
        1,
        Math.floor(Number(
          form?.querySelector('[name="quantity"]')?.value || 1
        )) || 1
      );
    }

    function selectedFinish() {
      return Array.from(root.querySelectorAll(
        '.gpo-radio input[type="radio"]:checked'
      )).find(input => {
        const field = input.closest('.gpo-element');

        if (
          input.disabled ||
          !field ||
          field.hidden ||
          !field.getClientRects().length
        ) {
          return false;
        }

        const choice = (
          input.getAttribute('original-value') ||
          input.value
        ).trim();

        return [
          data.unlabelled,
          data.supplied,
          data.applied
        ].some(name =>
          String(name || '').trim().toLowerCase() ===
          choice.toLowerCase()
        );
      });
    }

    function labelPrice(input) {
      const surcharge = input.getAttribute('data-addon-price');

      /* Direct Globo surcharges are in dollars. */
      if (surcharge !== null && surcharge.trim() !== '') {
        const amount = Number(surcharge);

        return Number.isFinite(amount)
          ? Math.round(amount * 100)
          : null;
      }

      /* Linked Shopify variant prices are in cents. */
      const linkedId = input.getAttribute('gpo-data-variant-id');

      if (linkedId) {
        const variant = labelVariants.find(item =>
          String(item.id) === String(linkedId)
        );

        return variant ? Number(variant.price) : null;
      }

      const choice = (
        input.getAttribute('original-value') ||
        input.value
      ).trim();

      return choice.toLowerCase() ===
        String(data.unlabelled || '').trim().toLowerCase()
        ? 0
        : null;
    }

    function updateSummary() {
      if (
        destroyed ||
        !root.isConnected ||
        !activeProduct ||
        !activeVariant
      ) {
        return;
      }

      const quantity = selectedQuantity();

      put(
        '[data-lb-pack-name]',
        packLabel(activeVariant) +
        (quantity > 1 ? ` × ${quantity} packs` : '')
      );

      put(
        '[data-lb-bag-price]',
        money(Number(activeVariant.price) * quantity)
      );

      const finish = initialised ? selectedFinish() : null;

      if (!finish) {
        put('[data-lb-finish]', 'Choose your label option');
        put('[data-lb-finish-help]', '');
        put('[data-lb-label-price]', '—');
        put('[data-lb-total]', '—');
        return;
      }

      put(
        '[data-lb-finish]',
        finish.getAttribute('original-value') ||
        finish.value
      );

      put(
        '[data-lb-finish-help]',
        finish.closest('.gpo-radio')
          ?.querySelector('.gpo-ov-helptext')
          ?.textContent.trim() || ''
      );

      const extra = labelPrice(finish);

      if (extra === null) {
        put('[data-lb-label-price]', 'Price unavailable');
        put('[data-lb-total]', '—');
        return;
      }

      put(
        '[data-lb-label-price]',
        money(extra * quantity)
      );

      put(
        '[data-lb-total]',
        money(
          (Number(activeVariant.price) + extra) * quantity
        )
      );
    }

    function scheduleSummary() {
      if (summaryPending || destroyed) return;

      summaryPending = true;

      summaryTimer = setTimeout(() => {
        summaryPending = false;
        updateSummary();
      }, 50);
    }

    /*
     * Retain the host's themed option set while purchasing
     * a variant from one of its connected bag products.
     */
    function checkHostOptionSet() {
      const assignedSet =
        app.productOptionSetMap?.[data.hostId];

      const assignedIds = String(assignedSet || '')
        .split(',')
        .map(value => value.trim());

      if (
        !hostOption ||
        app.option !== hostOption ||
        assignedSet == null ||
        !assignedIds.includes(String(hostOption.id))
      ) {
        throw new Error(
          'The host product must load its assigned occasion Globo set.'
        );
      }
    }

    function recordOccasion() {
      let input = form.querySelector(
        '[data-lb-occasion-property]'
      );

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Occasion]';
        input.setAttribute('data-lb-occasion-property', '');
        form.appendChild(input);
      }

      input.value = String(data.hostTitle || '')
        .replace(/\s*\(Personalised.*$/i, '')
        .trim();
    }

    function renderPacks(product) {
      if (!packsNode) {
        throw new Error(
          'The bag quantity container could not be found.'
        );
      }

      const fragment = document.createDocumentFragment();

      product.variants.forEach(variant => {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'lolly-pack-button';
        button.dataset.lbVariant = String(variant.id);
        button.textContent = packLabel(variant);
        button.disabled = !initialised || !variant.available;
        button.setAttribute('aria-pressed', 'false');

        fragment.appendChild(button);
      });

      packsNode.replaceChildren(fragment);
    }

    function updateSelectionDisplay() {
      if (!activeProduct || !activeVariant) return;

      builder.querySelectorAll('[data-lb-handle]').forEach(card => {
        card.setAttribute(
          'aria-pressed',
          String(
            card.dataset.lbHandle === activeProduct.handle
          )
        );
      });

      packsNode.querySelectorAll('[data-lb-variant]')
        .forEach(button => {
          button.setAttribute(
            'aria-pressed',
            String(
              button.dataset.lbVariant ===
              String(activeVariant.id)
            )
          );
        });

      const name = find('[data-lb-name]');

      if (name) {
        const text = shortName(activeProduct);

        if (name.textContent !== text) {
          name.textContent = text;
        }

        if (name.hidden) name.hidden = false;
      }

      const note = find('[data-lb-note]');

      if (note) {
        const text = contentsDescription(activeVariant.title);

        if (note.textContent !== text) {
          note.textContent = text;
        }

        if (note.hidden !== !text) {
          note.hidden = !text;
        }
      }
    }

    function updateBuyButton() {
      form.querySelectorAll(
        '[name="add"], button[type="submit"], input[type="submit"]'
      ).forEach(button => {
        button.disabled = !activeVariant.available;

        const text = activeVariant.available
          ? button.dataset.addToCartText ||
            window.theme?.strings?.addToCart ||
            'Add to cart'
          : window.theme?.strings?.noStock ||
            'Sold out';

        if (button.tagName === 'INPUT') {
          button.value = text;
          return;
        }

        const textNode = Array.from(button.childNodes)
          .find(node =>
            node.nodeType === Node.TEXT_NODE &&
            node.textContent.trim()
          );

        if (textNode) {
          if (textNode.textContent !== text) {
            textNode.textContent = text;
          }
        } else if (
          !button.querySelector('svg, .loading-spinner')
        ) {
          if (button.textContent !== text) {
            button.textContent = text;
          }
        }
      });
    }

    function applySelection(product, variant) {
      if (busy || failed || destroyed) return;

      checkHostOptionSet();
      busy = true;
      blockCart();

      try {
        const idInput = form.querySelector('[name="id"]');

        if (!idInput) {
          throw new Error(
            'The cart variant field could not be found.'
          );
        }

        activeProduct = product;
        activeVariant = variant;

        /*
         * Update the purchasable product without loading
         * that product's normal Globo option set.
         */
        app.product = product;
        app.form = form;

        idInput.disabled = false;
        idInput.value = String(variant.id);
        idInput.setAttribute('value', String(variant.id));

        form.querySelectorAll(
          '[name="product-id"], [name="product_id"]'
        ).forEach(input => {
          input.value = String(product.id);
          input.setAttribute('value', String(product.id));
        });

        window[`lollyActiveProduct_${sectionId}`] = product;

        app.handleCLO(
          hostOption.flatElements,
          true,
          { variant_title: variant.title }
        );

        app.syncGpoHiddenInputs(form);
        recordOccasion();

        idInput.dispatchEvent(
          new Event('change', { bubbles: true })
        );

        checkHostOptionSet();

        if (String(idInput.value) !== String(variant.id)) {
          throw new Error(
            'The selected cart variant did not match.'
          );
        }

        updateSelectionDisplay();
        updateSummary();
        updateBuyButton();

        builder.dataset.cartReady = String(
          Boolean(variant.available)
        );

        status('');

        root.dispatchEvent(
          new CustomEvent('lolly:product-change', {
            detail: { product, variant }
          })
        );

        scheduleSummary();
      } catch (error) {
        fail(error);
      } finally {
        busy = false;
      }
    }

    function switchProduct(handle) {
      if (!initialised || busy || failed || destroyed) return;

      const product = productMap.get(handle);

      if (!product || product === activeProduct) return;

      try {
        checkHostOptionSet();

        const previousCount = activeVariant
          ? packCount(activeVariant)
          : null;

        const variant =
          product.variants.find(item =>
            item.available &&
            previousCount &&
            packCount(item) === previousCount
          ) ||
          product.variants.find(item => item.available) ||
          product.variants[0];

        renderPacks(product);
        applySelection(product, variant);
      } catch (error) {
        fail(error);
      }
    }

    /*
     * The new Liquid section renders these blocks in place.
     * This also checks their containers before enabling purchase.
     */
    function moveBlocks() {
      const globoNode = root.querySelector(
        '.gpo-app, .gpo-container, .gpo-product-variants'
      );

      if (!globoNode || !form) return false;

      const globoSlot = find('[data-lb-globo-slot]');
      const buySlot = find('[data-lb-buy-slot]');

      if (!globoSlot || !buySlot) {
        throw new Error(
          'The builder layout containers could not be found.'
        );
      }

      const globoBlock =
        globoNode.closest('.product-info__block') ||
        globoNode;

      const buyBlock =
        form.closest('.product-info__block') ||
        form;

      if (
        globoBlock.contains(builder) ||
        buyBlock.contains(builder) ||
        globoBlock === buyBlock ||
        globoBlock.contains(buyBlock) ||
        buyBlock.contains(globoBlock)
      ) {
        throw new Error(
          'The Globo and buy-button containers need a layout adjustment.'
        );
      }

      if (globoBlock.parentElement !== globoSlot) {
        globoSlot.appendChild(globoBlock);
      }

      if (buyBlock.parentElement !== buySlot) {
        buySlot.appendChild(buyBlock);
      }

      return true;
    }

    function preparePreview() {
      if (previewPrepared || !products.length) return;

      activeProduct =
        products.find(product =>
          product.variants.some(variant => variant.available)
        ) ||
        products[0];

      activeVariant =
        activeProduct.variants.find(variant => variant.available) ||
        activeProduct.variants[0];

      renderPacks(activeProduct);
      updateSelectionDisplay();
      updateSummary();

      previewPrepared = true;
    }

    listen(builder, 'click', event => {
      if (!(event.target instanceof Element)) return;

      const card = event.target.closest('[data-lb-handle]');

      if (card) {
        event.preventDefault();
        switchProduct(card.dataset.lbHandle);
        return;
      }

      const button = event.target.closest('[data-lb-variant]');

      if (
        button &&
        initialised &&
        activeProduct &&
        !busy &&
        !failed
      ) {
        event.preventDefault();

        const variant = activeProduct.variants.find(item =>
          String(item.id) === button.dataset.lbVariant
        );

        if (variant?.available) {
          try {
            applySelection(activeProduct, variant);
          } catch (error) {
            fail(error);
          }
        }
      }
    });

    listen(root, 'change', scheduleSummary);
    listen(root, 'input', scheduleSummary);

    function guardCart(event) {
      if (!(event.target instanceof Element)) return;

      const isSubmit =
        event.type === 'submit' &&
        event.target.matches('.js-product-form-main');

      const isAddClick =
        event.type === 'click' &&
        event.target.closest(
          '.js-product-form-main [name="add"]'
        );

      if (!isSubmit && !isAddClick) return;

      const id = form?.querySelector('[name="id"]')?.value;

      const valid =
        initialised &&
        !busy &&
        !failed &&
        activeVariant?.available &&
        String(id) === String(activeVariant.id) &&
        String(activeProduct.id) !== String(data.hostId);

      if (!valid) {
        blockCart();
        event.preventDefault();
        event.stopImmediatePropagation();

        status('Please wait until your bag options are ready.');
        return;
      }

      try {
        checkHostOptionSet();
        app.syncGpoHiddenInputs(form);
        recordOccasion();

        if (
          String(form.querySelector('[name="id"]')?.value) !==
          String(activeVariant.id)
        ) {
          throw new Error(
            'The selected cart variant changed.'
          );
        }
      } catch (error) {
        event.preventDefault();
        event.stopImmediatePropagation();
        fail(error);
      }
    }

    listen(root, 'click', guardCart, true);
    listen(root, 'submit', guardCart, true);

    function initialise() {
      if (initialised || failed || destroyed) return true;

      form = root.querySelector('.js-product-form-main');
      blockCart();

      if (!products.length) {
        status(
          'Add bag products to the host’s Available lolly bags metafield.'
        );
        return true;
      }

      try {
        preparePreview();
      } catch (error) {
        fail(error);
        return true;
      }

      app = window.GPOConfigs;

      if (
        !form ||
        !root.querySelector('.gpo-app') ||
        !app ||
        typeof app.handleCLO !== 'function' ||
        typeof app.syncGpoHiddenInputs !== 'function' ||
        !Array.isArray(app.option?.flatElements) ||
        !app.productOptionSetMap ||
        app.option.id == null
      ) {
        return false;
      }

      const picker = root.querySelector(
        '.product-options variant-picker'
      );

      if (
        picker &&
        typeof picker.removeListeners !== 'function'
      ) {
        return false;
      }

      try {
        hostOption = app.option;
        checkHostOptionSet();

        if (!moveBlocks()) return false;

        if (picker) {
          picker.removeListeners();
          picker.dataset.updateUrl = 'false';
        }

        initialised = true;

        renderPacks(activeProduct);
        applySelection(activeProduct, activeVariant);

        if (failed) return true;

        priceObserver = new MutationObserver(scheduleSummary);

        priceObserver.observe(root.querySelector('.gpo-app'), {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: [
            'style',
            'class',
            'hidden',
            'disabled',
            'checked',
            'data-clo-display',
            'data-addon-price',
            'gpo-data-variant-id'
          ]
        });

        scheduleSummary();
        return true;
      } catch (error) {
        fail(error);
        return true;
      }
    }

    function start() {
      status('Loading your label options…');

      if (initialise()) return;

      const started = performance.now();

      startupTimer = setInterval(() => {
        if (destroyed || !root.isConnected) {
          clearInterval(startupTimer);
          priceObserver?.disconnect();
          return;
        }

        if (initialise()) {
          clearInterval(startupTimer);
        } else if (performance.now() - started > 15000) {
          clearInterval(startupTimer);
          blockCart();

          status(
            'The label options could not finish loading. ' +
            'Please refresh the page.'
          );
        }
      }, 150);
    }

    /* Sharper small Globo swatches */
    const swatchSelector =
      '.gpo-app .image-swatches img.swatch-image';

    function sharpenImage(img) {
      const source = img.getAttribute('src');
      if (!source) return;

      try {
        const url = new URL(source, window.location.href);

        if (
          url.origin !== window.location.origin ||
          !url.pathname.includes('/cdn/shop/files/')
        ) {
          return;
        }

        const requestedWidth = Number(
          url.searchParams.get('width')
        );

        if (requestedWidth >= 320) return;

        url.searchParams.set('width', '320');
        img.setAttribute('src', url.href);
      } catch (_) {
        // Leave other image URLs unchanged.
      }
    }

    function scanSwatches(node) {
      if (!(node instanceof Element)) return;

      if (node.matches(swatchSelector)) {
        sharpenImage(node);
      }

      node.querySelectorAll(swatchSelector)
        .forEach(sharpenImage);
    }

    const swatchObserver = new MutationObserver(mutations => {
      if (destroyed || !root.isConnected) {
        swatchObserver.disconnect();
        return;
      }

      mutations.forEach(mutation => {
        if (mutation.type === 'attributes') {
          if (
            mutation.target instanceof Element &&
            mutation.target.matches(swatchSelector)
          ) {
            sharpenImage(mutation.target);
          }
        } else {
          mutation.addedNodes.forEach(scanSwatches);
        }
      });
    });

    swatchObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src']
    });

    scanSwatches(root);

    /* Host-specific high-resolution label designs */
    const normaliseDesign = value => String(value || '')
      .normalize('NFKC')
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase('en-NZ');

    const designImages = new Map();

    try {
      const entries = JSON.parse(
        designData?.textContent || '[]'
      );

      if (Array.isArray(entries)) {
        entries.forEach(entry => {
          const name = normaliseDesign(entry.name);

          if (name && entry.image) {
            designImages.set(name, {
              name: String(entry.name).trim(),
              image: entry.image
            });
          }
        });
      }
    } catch (_) {
      console.warn(
        'Lolly builder: design preview data could not be read.'
      );
    }

    function updatePreviews() {
      if (destroyed || !root.isConnected) return;

      root.querySelectorAll(
        '.gpo-app .gpo-element:has(.image-swatches)'
      ).forEach(field => {
        const wrapper = field.querySelector('.gpo-ov-wrapper');

        if (!wrapper) return;

        let preview = field.querySelector(
          '.lolly-design-preview'
        );

        const selected = Array.from(field.querySelectorAll(
          '.image-swatches input[type="radio"]:checked'
        )).find(input => !input.disabled);

        const designName = (
          selected?.getAttribute('original-value') ||
          selected?.value ||
          ''
        ).trim();

        const design = designImages.get(
          normaliseDesign(designName)
        );

        const isUploadChoice =
          /\bupload\b.*\bown\b/i.test(designName);

        const fieldIsVisible =
          !field.hidden &&
          field.getClientRects().length > 0 &&
          getComputedStyle(field).display !== 'none';

        if (
          !selected ||
          !design ||
          isUploadChoice ||
          !fieldIsVisible
        ) {
          if (preview && !preview.hidden) {
            preview.hidden = true;
          }

          return;
        }

        if (!preview) {
          preview = document.createElement('div');
          preview.className = 'lolly-design-preview';
          preview.hidden = true;

          const title = document.createElement('p');
          title.className = 'lolly-design-preview__title';

          const image = document.createElement('img');
          image.loading = 'lazy';
          image.decoding = 'async';

          image.addEventListener('error', () => {
            image.dataset.failedSource =
              image.getAttribute('src') || '';

            preview.hidden = true;
          });

          preview.append(title, image);
          wrapper.insertAdjacentElement('afterend', preview);
        }

        const title = preview.querySelector(
          '.lolly-design-preview__title'
        );

        const titleText =
          `${data.previewPrefix || 'Your selected design:'} ` +
          designName;

        if (title.textContent !== titleText) {
          title.textContent = titleText;
        }

        const image = preview.querySelector('img');

        if (image.getAttribute('src') !== design.image) {
          delete image.dataset.failedSource;
          image.setAttribute('src', design.image);
        }

        if (image.alt !== designName) {
          image.alt = designName;
        }

        if (image.dataset.failedSource === design.image) {
          return;
        }

        if (preview.hidden) {
          preview.hidden = false;
        }
      });
    }

    let previewPending = false;
    let previewTimer;

    function schedulePreview() {
      if (previewPending || destroyed) return;

      previewPending = true;

      previewTimer = setTimeout(() => {
        previewPending = false;
        updatePreviews();
      }, 60);
    }

    listen(root, 'change', event => {
      if (
        event.target instanceof Element &&
        event.target.closest('.gpo-app')
      ) {
        schedulePreview();
      }
    });

    listen(root, 'lolly:product-change', schedulePreview);

    const previewObserver = new MutationObserver(mutations => {
      if (destroyed || !root.isConnected) {
        previewObserver.disconnect();
        return;
      }

      const relevant = mutations.some(mutation => {
        const target = mutation.target;

        if (
          !(target instanceof Element) ||
          target.closest('.lolly-design-preview')
        ) {
          return false;
        }

        if (target.closest('.gpo-app')) {
          return true;
        }

        return mutation.type === 'childList' &&
          Array.from(mutation.addedNodes).some(node =>
            node instanceof Element &&
            (
              node.matches('.gpo-app') ||
              node.querySelector('.gpo-app')
            )
          );
      });

      if (relevant) schedulePreview();
    });

    previewObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'disabled',
        'checked',
        'data-clo-display',
        'class',
        'style',
        'hidden'
      ]
    });

    schedulePreview();
    start();

    return () => {
      destroyed = true;

      aborter.abort();

      clearInterval(startupTimer);
      clearTimeout(summaryTimer);
      clearTimeout(previewTimer);

      priceObserver?.disconnect();
      swatchObserver.disconnect();
      previewObserver.disconnect();

      if (
        window[`lollyActiveProduct_${sectionId}`] ===
        activeProduct
      ) {
        delete window[`lollyActiveProduct_${sectionId}`];
      }
    };
  }

  function init(container) {
    const roots = Array.from(
      container.querySelectorAll('[data-lolly-themed-root]')
    );

    if (container.matches?.('[data-lolly-themed-root]')) {
      roots.unshift(container);
    }

    roots.forEach(root => {
      if (instances.has(root)) return;

      const cleanup = mount(root);

      if (cleanup) {
        instances.set(root, cleanup);
      }
    });
  }

  window.LollyThemedV2 = { init };

  document.addEventListener(
    'shopify:section:load',
    event => init(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    event => {
      instances.forEach((cleanup, root) => {
        if (event.target.contains(root)) {
          cleanup();
          instances.delete(root);
        }
      });
    }
  );

  init(document);
})();